<script setup lang="ts">
import Dialog from 'primevue/dialog';
import InputNumber from 'primevue/inputnumber';
import Select from 'primevue/select';
import Button from 'primevue/button';
import ProblemDetailsView from 'shared/problems/components/ProblemDetailsView.vue';
import { useEditInventoryStore } from '../stores/editInventoryStore';
import { storeToRefs } from 'pinia';
import { useLanguageStore } from 'shared/translations/stores/languageStore';
import { computed } from 'vue';
import { useConfirm } from 'primevue';
import { Assert } from 'shared/assert';

const languageStore = useLanguageStore();
const { translations } = storeToRefs(languageStore);
const editInventoryStore = useEditInventoryStore();
const confirm = useConfirm();


const {
  visible,
  problems,
  selectedProductId,
  currentCount,
  newCount,
  saving,
  productOptions,
  logYard,
  storageAreaName,
  selectedProductName
} = storeToRefs(editInventoryStore);


const saveBtnWarning = computed<boolean>(() => {
  if(!logYard.value || !selectedProductId.value || logYard.value.content.length === 0) return false;

  if (logYard.value.allowedProducts.length === 0 && logYard.value.allowMultipleProducts === false) {
    if (logYard.value.content.some(p => p.productId === selectedProductId.value)) {
      return false;
    } else {
      return true;
    }
  }
return false;
});

function onEmptyClick() {
  newCount.value = 0;
  editInventoryStore.save();
}

function onSaveClick() {
  if (saveBtnWarning.value) {
    Assert.definied(storageAreaName.value);
    Assert.definied(selectedProductName.value);

    confirm.require({
      header: translations.value.dialogs.inventory.confirmHeader(storageAreaName.value),
      message: translations.value.dialogs.inventory.confirmMessage(storageAreaName.value, selectedProductName.value),
      icon: 'pi pi-exclamation-triangle',
      rejectLabel: translations.value.generic.abort,
      rejectProps: {
        label: translations.value.generic.abort,
        severity: 'secondary',
        outlined: true,
      },
      acceptProps: {
        label: translations.value.generic.confirm,
        severity: 'warn',
      },
      accept: () => {
        editInventoryStore.save();
      },
    });
  } else {
    editInventoryStore.save();
  }
}


</script>

<template>
  <Dialog v-model:visible="visible" :header="translations.dialogs.inventory.title + ' ' + storageAreaName" :modal="false" :draggable="true"
    :closable="true" id="log-yard-edit-inventory-dialog">
    <ProblemDetailsView v-model:model-value="problems"></ProblemDetailsView>

    <div class="field">
      <label for="product-select"> {{ translations.dialogs.inventory.selectProduct }} </label>
      <Select id="log-yard-edit-inventory-current-count-input" v-model="selectedProductId"
        :options="productOptions" option-label="name" option-value="id">
      </Select>
    </div>

    <div v-if="selectedProductId" class="current-amount">
      <label for="log-yard-edit-inventory-current-count-input">
        {{ translations.dialogs.inventory.currentAmount }}
      </label>
      <span>{{ currentCount }}</span>
    </div>

    <div v-if="selectedProductId" class="field">
      <label for="log-yard-edit-inventory-new-count-input">
        {{ translations.dialogs.inventory.setNewAmount }}
      </label>
      <InputNumber pt:pcInputText:root:autofocus v-model:model-value="newCount"
        id="log-yard-edit-inventory-new-count-input" class="number-field"></InputNumber>
    </div>

    <div class="actions">
      <Button v-if="selectedProductId" :label="translations.generic.empty" :loading="saving" @click="onEmptyClick"></Button>
      <Button icon="pi pi-save" :label="translations.generic.save" :severity="saveBtnWarning ? 'warn' : 'primary' " :loading="saving"
        v-if="selectedProductId" @click="onSaveClick"></Button>
    </div>
  </Dialog>
</template>

<style scoped>
.field {
  display: grid;
  grid-template-rows: repeat(2, auto);
}

.actions {
  display: flex;
  justify-content: end;
  column-gap: .5rem;
}

.current-amount {
  display: grid;
  column-gap: .5rem;
  grid-template-columns: auto 1fr;
}

.current-amount span {
  text-align: start;
}

:deep(.p-inputnumber-input) {
  text-align: end;
}
</style>

<style>
#log-yard-edit-inventory-dialog {
  min-width: 20rem;
}

#log-yard-edit-inventory-dialog .p-dialog-content {
  display: flex;
  grid-template-columns: 1fr;
  flex-direction: column;
  gap: 1rem;
}
</style>
